import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Button,
    IconButton,
    Tooltip,
    TextField,
    Divider,
    Drawer,
    Chip,
    Avatar,
    Fade,
    useTheme,
    CircularProgress
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SearchIcon from '@mui/icons-material/Search';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';

const ContractManagementPage = () => {
    const theme = useTheme();
    const { showToast } = useToast();
    const [contracts, setContracts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedContract, setSelectedContract] = useState<any | null>(null);
    const [editMode, setEditMode] = useState(false);
    const [editedContent, setEditedContent] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchContracts();
    }, []);

    const fetchContracts = async () => {
        try {
            setLoading(true);
            const res = await api.get('/api/contract');
            setContracts(res.data);
        } catch (err) {
            console.error('Error fetching contracts:', err);
            showToast('Erreur lors du chargement des contrats', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectContract = (contract: any) => {
        setSelectedContract(contract);
        setEditedContent(contract.content);
        setEditMode(false);
        setDrawerOpen(true);
    };

    const handleSave = async () => {
        if (!selectedContract) return;
        try {
            const res = await api.put(`/api/contract/${selectedContract._id}`, {
                content: editedContent
            });
            setSelectedContract(res.data);
            setContracts(contracts.map(c => c._id === res.data._id ? res.data : c));
            setEditMode(false);
            showToast('Contrat mis à jour avec succès', 'success');
        } catch (err) {
            console.error('Error updating contract:', err);
            showToast('Erreur lors de la mise à jour', 'error');
        }
    };

    const handleDownloadPdf = async (id: string, name: string) => {
        try {
            const res = await api.get(`/api/contract/${id}/pdf`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            const fileName = name ? name.replace(/\s+/g, '_') : 'Contrat';
            link.setAttribute('download', `Contrat_${fileName}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error('Error downloading PDF:', err);
            showToast('Erreur lors du téléchargement du PDF', 'error');
        }
    };

    const handleSendEmail = async (id: string) => {
        try {
            await api.post(`/api/contract/${id}/send`);
            showToast('Contrat envoyé avec succès', 'success');
        } catch (err) {
            console.error('Error sending email:', err);
            showToast("Erreur lors de l'envoi de l'email", 'error');
        }
    };

    const filteredContracts = contracts.filter(c =>
        c.organization?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.client?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
            p: { xs: 2, md: 4 }
        }}>
            {/* Header Area */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 4,
                    p: { xs: 3, md: 5 },
                    mb: 4,
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56, backdropFilter: 'blur(10px)' }}>
                        <AssignmentIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>
                            Gestion des Contrats
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                            Gérez, modifiez et envoyez vos contrats de formation
                        </Typography>
                    </Box>
                </Box>

                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Rechercher un contrat (par organisation ou client)..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ color: 'white', mr: 1, opacity: 0.8 }} />,
                        sx: {
                            bgcolor: 'rgba(255,255,255,0.15)',
                            color: 'white',
                            borderRadius: 3,
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                            '& input::placeholder': { color: 'rgba(255,255,255,0.7)', opacity: 1 }
                        }
                    }}
                />
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress sx={{ color: '#667eea' }} />
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {filteredContracts.map((contract, index) => (
                        <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={contract._id}>
                            <Fade in timeout={300 + index * 100}>
                                <Card sx={{
                                    height: '100%',
                                    borderRadius: 4,
                                    border: '1px solid rgba(102, 126, 234, 0.2)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: '0 12px 40px rgba(102, 126, 234, 0.25)',
                                    }
                                }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                            <Chip
                                                label="Contrat Généré"
                                                size="small"
                                                sx={{ bgcolor: 'rgba(72, 187, 120, 0.1)', color: '#2f855a', fontWeight: 600 }}
                                            />
                                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <CalendarTodayIcon sx={{ fontSize: 14 }} />
                                                {new Date(contract.createdAt).toLocaleDateString('fr-FR')}
                                            </Typography>
                                        </Box>

                                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#2d3748' }}>
                                            {contract.organization?.name || 'Inconnue'}
                                        </Typography>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <BusinessIcon sx={{ fontSize: 18, color: '#667eea' }} />
                                            <Typography variant="body2" color="text.secondary">
                                                Client: {contract.client?.name}
                                            </Typography>
                                        </Box>

                                        <Divider sx={{ mb: 2 }} />

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Tooltip title="Voir / Modifier">
                                                <IconButton
                                                    onClick={() => handleSelectContract(contract)}
                                                    sx={{ color: '#667eea', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.1)' } }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Box>
                                                <Tooltip title="Télécharger PDF">
                                                    <IconButton
                                                        onClick={() => handleDownloadPdf(contract._id, contract.organization?.name || 'Contrat')}
                                                        sx={{ color: '#48bb78', '&:hover': { bgcolor: 'rgba(72, 187, 120, 0.1)' } }}
                                                    >
                                                        <DownloadIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Envoyer par Email">
                                                    <IconButton
                                                        onClick={() => handleSendEmail(contract._id)}
                                                        sx={{ color: '#764ba2', '&:hover': { bgcolor: 'rgba(118, 75, 162, 0.1)' } }}
                                                    >
                                                        <SendIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Fade>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Contract Edit/Preview Drawer */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{
                    sx: { width: { xs: '100%', md: 600 }, p: 0 }
                }}
            >
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{
                        p: 3,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {selectedContract?.organization?.name}
                        </Typography>
                        <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
                        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                Contenu du Contrat
                            </Typography>
                            <Button
                                variant={editMode ? "contained" : "outlined"}
                                startIcon={editMode ? <SaveIcon /> : <EditIcon />}
                                onClick={() => editMode ? handleSave() : setEditMode(true)}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    ...(editMode ? {
                                        bgcolor: '#48bb78',
                                        '&:hover': { bgcolor: '#38a169' }
                                    } : {
                                        color: '#667eea',
                                        borderColor: '#667eea'
                                    })
                                }}
                            >
                                {editMode ? 'Enregistrer' : 'Modifier'}
                            </Button>
                        </Box>

                        {editMode ? (
                            <TextField
                                fullWidth
                                multiline
                                minRows={20}
                                value={editedContent}
                                onChange={(e) => setEditedContent(e.target.value)}
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        fontFamily: 'monospace',
                                        fontSize: '0.8rem',
                                        bgcolor: '#f8fafc'
                                    }
                                }}
                            />
                        ) : (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    bgcolor: 'white',
                                    overflowX: 'auto',
                                    '& .contract-preview-container': { transform: 'scale(0.8)', transformOrigin: 'top center', boxShadow: 'none', border: 'none', p: 0 }
                                }}
                            >
                                <div dangerouslySetInnerHTML={{ __html: selectedContract?.content }} />
                            </Paper>
                        )}
                    </Box>

                    <Box sx={{ p: 3, borderTop: '1px solid #e2e8f0', display: 'flex', gap: 2 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownloadPdf(selectedContract?._id, selectedContract?.organization?.name)}
                            sx={{ bgcolor: '#48bb78', '&:hover': { bgcolor: '#38a169' }, borderRadius: 2 }}
                        >
                            Télécharger
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<SendIcon />}
                            onClick={() => handleSendEmail(selectedContract?._id)}
                            sx={{ bgcolor: '#764ba2', '&:hover': { bgcolor: '#5a3791' }, borderRadius: 2 }}
                        >
                            Envoyer
                        </Button>
                    </Box>
                </Box>
            </Drawer>
        </Box>
    );
};

export default ContractManagementPage;
